import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Search, X, Download, AlertTriangle } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm bg-white";
const label = "block text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5";

const COMPANY_OPTIONS = [
  { key: "misas", label: "Mis-aş Gıda İnş. San. Ve Tic. A.Ş" },
  { key: "mis_holding", label: "Mis Holding A.Ş." },
  { key: "koop", label: "S.S. Mis Tüketim Kooperatifi" },
];

export default function DevirDialog({ open, onClose, transferor, onSaved }) {
  const [company, setCompany] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [transferee, setTransferee] = useState(null);
  const [hisse, setHisse] = useState("");
  const [busy, setBusy] = useState(false);
  const [dateStr, setDateStr] = useState(() => new Date().toLocaleDateString("tr-TR"));
  const [ykKarar, setYkKarar] = useState("");
  const [format, setFormat] = useState("docx"); // "docx" | "pdf"
  const [saveToDb, setSaveToDb] = useState(false);

  const available = useMemo(() => {
    if (!transferor) return [];
    return COMPANY_OPTIONS.filter(c => Number(transferor[`${c.key}_hisse`] || 0) > 0);
  }, [transferor]);

  useEffect(() => {
    if (open) {
      setCompany(available[0] || null);
      setTransferee(null); setQ(""); setResults([]); setHisse("");
      setDateStr(new Date().toLocaleDateString("tr-TR"));
      setYkKarar(""); setFormat("docx"); setSaveToDb(false);
    }
  }, [open, available]);

  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/members`, { params: { status: "active", q: q.trim() } });
        setResults(r.data.filter(m => m.id !== transferor?.id).slice(0, 8));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, transferor]);

  if (!open) return null;

  const maxHisse = company ? Number(transferor?.[`${company.key}_hisse`] || 0) : 0;

  const submit = async () => {
    if (!company) { toast.error("Şirket seçin"); return; }
    if (!transferee) { toast.error("Devir alan seçin"); return; }
    const n = Number(hisse);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Geçerli hisse adedi girin"); return; }
    if (n > maxHisse) { toast.error(`En fazla ${maxHisse} hisse devredilebilir`); return; }
    if (saveToDb && !ykKarar.trim()) {
      toast.error("DB'ye kaydetmek için Yönetim Kurulu Karar No zorunludur");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/devir/contract`, {
        company: company.key,
        transferor_id: transferor.id,
        transferee_id: transferee.id,
        hisse: n,
        date_str: dateStr,
        yk_karar_no: ykKarar.trim(),
        format,
        save_to_db: saveToDb,
      }, { responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `devir-sozlesmesi.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      if (saveToDb) {
        toast.success("Devir DB'ye kaydedildi ve sözleşme indirildi");
        onSaved?.();
      } else {
        toast.success(format === "pdf" ? "Devir sözleşmesi PDF olarak indirildi" : "Devir sözleşmesi indirildi");
      }
      onClose();
    } catch (err) {
      let msg = "Sözleşme oluşturulamadı";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text); msg = j.detail || msg;
        } catch (_) { /* ignore */ }
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="devir-dialog">
      <div className="crisp-card p-6 w-full max-w-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Devir Sözleşmesi Oluştur</h3>
            <p className="text-xs text-slate-500 mt-0.5">Devir eden: <span className="font-semibold text-slate-700">{transferor?.ad_soyad}</span></p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={18}/></button>
        </div>

        {available.length === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
            Bu ortağın devredebileceği aktif hissesi bulunmuyor.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <span className={label}>Şirket</span>
              <div className="flex flex-wrap gap-2">
                {available.map(c => (
                  <button key={c.key} type="button" onClick={() => setCompany(c)}
                    data-testid={`devir-co-${c.key}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${company?.key === c.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                    {c.label} · {transferor[`${c.key}_hisse`]} hisse
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className={label}>Devir Alan (aktif ortaklar arasında ara)</span>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="devir-search" className={inputCls + " pl-9"} value={q}
                       onChange={e => { setQ(e.target.value); setTransferee(null); }}
                       placeholder="En az 2 karakter yazın (ad veya TC)" />
              </div>
              {transferee ? (
                <div className="mt-2 flex items-center justify-between bg-slate-900 text-white rounded-md px-3 py-2">
                  <div>
                    <div className="text-sm font-bold">{transferee.ad_soyad}</div>
                    <div className="text-[11px] text-slate-300 font-mono-nums">{transferee.tc_kimlik || "—"}</div>
                  </div>
                  <button onClick={() => { setTransferee(null); setQ(""); }} className="text-slate-300 hover:text-white text-xs">Değiştir</button>
                </div>
              ) : results.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-y-auto">
                  {results.map(r => (
                    <button key={r.id} type="button" onClick={() => { setTransferee(r); setQ(""); setResults([]); }}
                      data-testid={`devir-pick-${r.id}`}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0">
                      <div className="text-sm font-semibold text-slate-900">{r.ad_soyad}</div>
                      <div className="text-[11px] text-slate-500 font-mono-nums">{r.tc_kimlik || "—"}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className={label}>Devredilecek Hisse</span>
                <input data-testid="devir-hisse" type="number" min="1" max={maxHisse} className={inputCls}
                       value={hisse} onChange={e => setHisse(e.target.value)} placeholder={`En fazla ${maxHisse}`} />
              </div>
              <div>
                <span className={label}>Tarih</span>
                <input data-testid="devir-date" className={inputCls} value={dateStr} onChange={e => setDateStr(e.target.value)} placeholder="gg.aa.yyyy" />
              </div>
            </div>

            <div>
              <span className={label}>Yönetim Kurulu Karar No {saveToDb && <span className="text-red-600 normal-case tracking-normal font-normal">(zorunlu)</span>}</span>
              <input data-testid="devir-yk" className={inputCls} value={ykKarar}
                     onChange={e => setYkKarar(e.target.value)} placeholder="Ör. 2026/07" />
            </div>

            <div>
              <span className={label}>Belge Formatı</span>
              <div className="inline-flex rounded-md overflow-hidden border border-slate-200">
                <button type="button" onClick={() => setFormat("docx")} data-testid="devir-fmt-docx"
                  className={`px-4 py-1.5 text-xs font-bold ${format === "docx" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>DOCX</button>
                <button type="button" onClick={() => setFormat("pdf")} data-testid="devir-fmt-pdf"
                  className={`px-4 py-1.5 text-xs font-bold border-l border-slate-200 ${format === "pdf" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>PDF (kilitli)</button>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {format === "pdf"
                  ? "Alıcı düzenleyemez; sadece görüntüleme ve yazdırma açıktır."
                  : "Standart Word (DOCX) belgesi — açıp düzenlenebilir."}
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" data-testid="devir-save-db" className="mt-0.5 h-4 w-4 accent-slate-900"
                     checked={saveToDb} onChange={e => setSaveToDb(e.target.checked)} />
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-900">Onayla ve DB'ye kaydet</div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  İşaretlerseniz, bu devir hemen veritabanına işlenir: devredenin hissesi düşer, devralana eklenir ve <b>Değişiklik Geçmişi</b>ne eşleştirilmiş kayıt olarak yazılır.
                </div>
                {saveToDb && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                    <AlertTriangle size={12} className="mt-0.5"/>
                    Bu işlem geri alınamaz — hisseler kalıcı olarak güncellenir.
                  </div>
                )}
              </div>
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50">İptal</button>
              <button onClick={submit} disabled={busy} data-testid="devir-download"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60">
                <Download size={14}/> {busy ? "Hazırlanıyor..." : (saveToDb ? "Onayla ve İndir" : (format === "pdf" ? "PDF Olarak İndir" : "Word Olarak İndir"))}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
